// src/components/HomePage.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { event } from '../utils/gtm';
import './HomePage.css';
import CreditUsageWidget from './CreditUsageWidget';
import TrackedChannelsWidget from './TrackedChannelsWidget';

// We'll re-use the same icons from the sidebar for consistency
const icons = {
  keyword: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
      <defs>
        <linearGradient id="grad-keyword" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1d94d0" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path stroke="url(#grad-keyword)" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  ),
  channel: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
      <defs>
        <linearGradient id="grad-channel" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1d94d0" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path stroke="url(#grad-channel)" strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  script: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
      <defs>
        <linearGradient id="grad-script" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1d94d0" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path stroke="url(#grad-script)" strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  locked: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
      <defs>
        <linearGradient id="grad-locked" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6b7280" />
          <stop offset="100%" stopColor="#9ca3af" />
        </linearGradient>
      </defs>
      <path stroke="url(#grad-locked)" strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  ),
  gap: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
      <defs>
        <linearGradient id="grad-gap" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1d94d0" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path stroke="url(#grad-gap)" strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.572L16.5 21.75l-.398-1.178a3.375 3.375 0 00-2.455-2.456L12.75 18l1.178-.398a3.375 3.375 0 002.455-2.456L16.5 14.25l.398 1.178a3.375 3.375 0 002.456 2.456L20.25 18l-1.178.398a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  ),
};

const HomePage = ({ setActiveTool, user, onCompetitorClick }) => {
  const navigate = useNavigate();
  return (
    <div className="home-page-container">
      <div className="home-header">
        {user?.plan === 'pro' ? (
          <h1 className="mb-12">Welcome back, {user.name}!</h1>
        ) : (
          <>
            <h1>Welcome, {user.name}!</h1>
            <p>
              🚀 Ready for more power?{' '}
              <Link to="/pricing" className="text-blue-500 hover:underline" onClick={() => event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Homepage Upsell' })}>
                Unlock all features with a Pro plan.
              </Link>
            </p>
          </>
        )}
      </div>
 
      {/* --- Dashboard Widget Section --- */}
      <div className="dashboard-section">
        <div className="overview-panel">
          <h2 className="panel-title">Dashboard Overview</h2>
          <div className="widget-grid">
            <CreditUsageWidget user={user} />
            <TrackedChannelsWidget onCompetitorClick={onCompetitorClick} />
          </div>
        </div>
      </div>
 
      {/* --- AI Toolkit Section --- */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">Your AI Toolkit</h2>
        <div className="tool-card-grid">
          {/* Keyword Research Card */}
          <div className="tool-card" onClick={() => {
              event({ action: 'click_nav', category: 'Navigation', label: 'Homepage Card: Keyword Research' });
              setActiveTool('keyword-research');
            }}>
          <div className="tool-card-inner">
            {icons.keyword}
            <h3>Keyword Research</h3>
            <p>Discover high-potential keywords and analyze top-ranking videos.</p>
          </div>
        </div>
        {/* Channel Analyzer Card */}
        <div className="tool-card" onClick={() => {
            event({ action: 'click_nav', category: 'Navigation', label: 'Homepage Card: Channel Analyzer' });
            setActiveTool('channel-analyzer');
          }}>
          <div className="tool-card-inner">
            {icons.channel}
            <h3>Channel Analyzer</h3>
            <p>Get a full intelligence report on any channel, including their top videos and recent trends.</p>
          </div>
        </div>
        {/* Script Intelligence Card */}
        <div className="tool-card" onClick={() => {
            event({ action: 'click_nav', category: 'Navigation', label: 'Homepage Card: Script Intelligence' });
            setActiveTool('script-intelligence');
          }}>
          <div className="tool-card-inner">
            {icons.script}
            <h3>Script Intelligence</h3>
            <p>Optimize your video before you film with AI-generated titles, descriptions, and thumbnails.</p>
          </div>
        </div>
        {/* Card 4: Dynamic Content Gap Finder */}
        {user.plan === 'pro' ? (
          // Pro Version: Coming Soon
          <div className="tool-card coming-soon-card">
            <div className="coming-soon-badge">Coming Soon</div>
            <div className="tool-card-inner">
              {icons.gap}
              <h3>Content Gap Finder</h3>
              <p>Discover high-potential video ideas your competitors are missing.</p>
            </div>
          </div>
        ) : (
          // Free Version: Locked
          <div className="tool-card coming-soon-card locked-card" onClick={() => {
              event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Content Gap Finder Card' });
              navigate('/pricing');
            }}>
            <div className="coming-soon-badge">Coming Soon</div>
            <div className="tool-card-inner">
              {icons.locked}
              <h3>Content Gap Finder (Pro)</h3>
              <p>Discover high-potential video ideas your competitors are missing.</p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;